import { DateTime } from "luxon";

const formatTime12Hour = (dateTime: DateTime): string => {
  const hour = dateTime.hour;
  const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  const minute = dateTime.minute.toString().padStart(2, "0");
  return `${hour12}:${minute}`;
};

const formatSingleTime = (dateTime: DateTime): string => {
  return dateTime
    .toLocaleString({
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    })
    .toLowerCase()
    .replace(" ", "");
};

export const formatCalendarEventTime = (
  startTime: string | undefined | null,
  endTime: string | undefined | null,
  isAllDay: boolean = false
): string => {
  if (isAllDay) return "All day";
  if (!startTime) return "Unknown time";

  const startDateTime = DateTime.fromISO(startTime);
  if (!startDateTime.isValid) return "Invalid time";

  if (!endTime) return formatSingleTime(startDateTime);

  const endDateTime = DateTime.fromISO(endTime);
  if (!endDateTime.isValid) return formatSingleTime(startDateTime);

  if (startDateTime.hasSame(endDateTime, "minute")) return "";

  const startTimeStr = formatTime12Hour(startDateTime);
  const endTimeStr = formatTime12Hour(endDateTime);
  const startIsAM = startDateTime.hour < 12;
  const endIsAM = endDateTime.hour < 12;

  // Format AM/PM: don't repeat if both are the same period
  let formattedStart = startTimeStr;
  let formattedEnd = endTimeStr;

  if (startIsAM && endIsAM) {
    // Both AM - am only on end
    formattedEnd += "am";
  } else if (!startIsAM && !endIsAM) {
    // Both PM - pm only on end
    formattedEnd += "pm";
  } else {
    // Mixed - show both
    formattedStart += startIsAM ? "am" : "pm";
    formattedEnd += endIsAM ? "am" : "pm";
  }

  if (startDateTime.hasSame(endDateTime, "day")) {
    return `${formattedStart}-${formattedEnd}`;
  }

  // Different days - show dates with times
  const formatOptions = {
    month: "short" as const,
    day: "numeric" as const,
    hour: "numeric" as const,
    minute: "2-digit" as const,
  };
  return `${startDateTime.toLocaleString(formatOptions)} - ${endDateTime.toLocaleString(formatOptions)}`;
};

export const formatCalendarEventDate = (startTime: string): string => {
  if (!startTime) return "";

  const startDateTime = DateTime.fromISO(startTime);
  if (!startDateTime.isValid) return "";

  return startDateTime.toLocaleString({
    weekday: "long",
    month: "short",
    day: "numeric",
  });
};

const HAPPENING_NOW_THRESHOLD_MINUTES = 5;

const formatDuration = (minutes: number): string => {
  if (minutes < 60) {
    return `${minutes} min`;
  }
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  if (remainingMinutes === 0) {
    return hours === 1 ? "1 hr" : `${hours} hrs`;
  }
  return hours === 1 ? `1 hr ${remainingMinutes} min` : `${hours} hrs ${remainingMinutes} min`;
};

export const getImminentEventTimeLabel = (
  startTime: DateTime,
  endTime?: DateTime | null
): string => {
  const now = DateTime.now();
  const startDiffMinutes = startTime.diff(now, "minutes").minutes;
  const startRounded = Math.round(startDiffMinutes);

  // Event hasn't started yet
  if (startDiffMinutes > 0) {
    if (startRounded === 0) {
      return "Happening now";
    }
    return `Starts in ${formatDuration(startRounded)}`;
  }

  // Event has started
  const minutesSinceStart = Math.abs(startRounded);

  // Show "Happening now" for meetings that started within the last 5 minutes
  if (minutesSinceStart <= HAPPENING_NOW_THRESHOLD_MINUTES) {
    return "Happening now";
  }

  // Check end time
  if (endTime) {
    const endDiffMinutes = endTime.diff(now, "minutes").minutes;
    const endRounded = Math.round(endDiffMinutes);

    // Event is ongoing - show "Ends in"
    if (endDiffMinutes > 0) {
      return `Ends in ${formatDuration(endRounded)}`;
    }

    // Event has ended - show "Ended X min ago"
    const minutesSinceEnd = Math.abs(endRounded);
    return `Ended ${formatDuration(minutesSinceEnd)} ago`;
  }

  return `Started ${formatDuration(minutesSinceStart)} ago`;
};
